import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiCamera, FiCheckCircle, FiChevronDown, FiChevronUp, FiPlus, FiX } from "react-icons/fi";
import {
  createDealerSubmission,
  getCarMakes,
  publishOwnDealerSubmission,
  uploadDealerSubmissionPhoto,
  DealerSubmissionPublishPayload,
} from "api/services/dealer";
import { getDealerPortalPath, Paths } from "router/Paths";
import { CarMakeType } from "types/loadedData";

const fuelOptions = ["Electric", "Hybrid", "Gas", "Diesel"];

const digitsOnly = (value: string | number) =>
  String(value).replace(/\D/g, "");

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const formatNumber = (value: string) => {
  const digits = onlyDigits(value);
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

const hasContactInfo = (value: string) =>
  /(https?:\/\/|www\.|t\.me\/|telegram\.me\/|wa\.me\/|\.com\b)/i.test(value) ||
  /(?:\+?\d[\s().-]*){8,}/.test(value);

const getStoredDealer = () => {
  try {
    const value = localStorage.getItem("dealer");
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

function DealerDealPostPage() {
  const [dealer] = useState<any | null>(getStoredDealer);
  
  useEffect(() => {
    if (!dealer) {
      window.location.assign(
        `${getDealerPortalPath(Paths.dealerLogin)}?next=${encodeURIComponent(
          getDealerPortalPath(Paths.dealerDealPost)
        )}`
      );
    }
  }, [dealer]);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      Array.from({ length: currentYear + 1 - 1990 }, (_, i) => currentYear + 1 - i),
    [currentYear]
  );

  const [language, setLanguage] = useState<"am" | "en">("en");
  const [carMakes, setCarMakes] = useState<CarMakeType[]>([]);

  useEffect(() => {
    getCarMakes().then(setCarMakes).catch(() => {});
  }, []);

  const [images, setImages] = useState<File[]>([]);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [fuelType, setFuelType] = useState("Electric");
  const [color, setColor] = useState("");
  const [batteryRangeKm, setBatteryRangeKm] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [wasPublished, setWasPublished] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const canSubmit = useMemo(() => {
    if (images.length === 0 || !make.trim() || !model.trim() || !fuelType) {
      return false;
    }
    if (!onlyDigits(year) || !onlyDigits(price)) return false;
    return !saving;
  }, [fuelType, images, make, model, price, saving, year]);

  const chooseImage = (event: ChangeEvent<HTMLInputElement>) => {
    const nextImages = event.target.files;
    if (!nextImages || nextImages.length === 0) return;
    const newImages = Array.from(nextImages);
    setImages([newImages[0]]);
    event.target.value = "";
    setMessage("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (images.length === 0) {
      setMessage("Add at least one car image.");
      return;
    }

    const parsedYear = Number(onlyDigits(year));
    if (!parsedYear || parsedYear < 2000) {
      setMessage("Year must be 2000 or newer.");
      return;
    }
    if (hasContactInfo(description)) {
      setMessage("Description cannot include phone numbers or links.");
      return;
    }

    setSaving(true);
    setMessage("");

    const shouldDirectPost = Boolean(dealer?.trustedDealer);
    const directDetailsComplete = Boolean(
      make && model && fuelType
    );

    try {
      const submission = await createDealerSubmission({
        isDeal: true,
        price: parsedYear ? digitsOnly(price) : "",
        year: parsedYear,
        makeModelText: [carMakes.find((m) => m._id === make)?.name, model.trim()]
          .filter(Boolean)
          .join(" "),
        fuelType,
        color: color.trim() || "Unspecified",
        batteryRangeKm:
          fuelType === "Electric" ? digitsOnly(batteryRangeKm) : "",
        note: description.trim(),
        make,
        model: model.trim(),
      });

      for (let i = 0; i < images.length; i++) {
        await uploadDealerSubmissionPhoto(submission._id, "deal", images[i], i);
      }

      if (shouldDirectPost && directDetailsComplete) {
        const publishPayload: DealerSubmissionPublishPayload = {
          make,
          model: model.trim(),
          year: String(parsedYear),
          price: digitsOnly(price),
          fuelType,
          color: color.trim() || "Unspecified",
          isDeal: true,
          batteryRangeKm:
            fuelType === "Electric" ? digitsOnly(batteryRangeKm) : "",
          description: description.trim(),
        };
        await publishOwnDealerSubmission(submission._id, publishPayload);
        setWasPublished(true);
      } else {
        setWasPublished(false);
      }

      setSent(true);
    } catch (error: any) {
      setMessage(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Could not send this deal. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setImages([]);
    setMake("");
    setModel("");
    setYear("");
    setFuelType("Electric");
    setColor("");
    setBatteryRangeKm("");
    setPrice("");
    setDescription("");
    setSent(false);
    setWasPublished(false);
    setMessage("");
  };

  return (
    <main className={ClassName.page}>
      <section className={ClassName.shell}>
        <header className={ClassName.postHeader}>
          <button
            type="button"
            className={ClassName.backButton}
            onClick={() => window.location.assign(getDealerPortalPath(Paths.home))}
          >
            <FiArrowLeft />
          </button>
          <h1 className={ClassName.postTitle}>
            {language === "am" ? "የቅናሽ ድርድር ይለጥፉ" : "Post a discount deal"}
          </h1>
          <button
            type="button"
            className={ClassName.langMini}
            onClick={() => setLanguage((current) => (current === "am" ? "en" : "am"))}
          >
            {language === "am" ? "En" : "አማ"}
          </button>
        </header>

        <div className={ClassName.postPage}>
          {sent ? (
            <div className="mx-4 mt-6 rounded-[20px] bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-green-800">
                <FiCheckCircle className="mt-0.5 h-7 w-7 shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">
                    {wasPublished ? "Deal Published" : "Sent to BossCar"}
                  </h2>
                  <p className="mt-1 text-sm leading-5">
                    {wasPublished
                      ? "Your discount deal is now live on the marketplace."
                      : "Staff will review the discount and contact you if it is ready."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={reset}
                className="mt-5 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-[13px] bg-primary px-4 text-base font-semibold text-white shadow-sm active:scale-[0.99]"
              >
                <FiPlus />
                Send another deal
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4 mt-4">
              <section className={ClassName.card}>
                <h2 className={ClassName.sectionTitle}>
                  {language === "am"
                    ? "የመኪናውን 1 ፎቶ ያክሉ"
                    : "Add 1 photo of the car"}
                </h2>
                <div className={ClassName.photoActionRow}>
                  <label className={`${ClassName.photoActionTile} ${ClassName.photoActionAdd}`}>
                    <FiPlus className={ClassName.photoActionPlus} />
                    <input
                      className="hidden"
                      type="file"
                      accept="image/*"
                      onChange={chooseImage}
                    />
                  </label>
                  <label className={ClassName.photoActionTile}>
                    <FiCamera className={ClassName.photoActionCamera} />
                    <span>{language === "am" ? "ፎቶ አንሳ" : "Take Photo"}</span>
                    <input
                      className="hidden"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={chooseImage}
                    />
                  </label>
                </div>
                {images.length > 0 && (
                  <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
                    {images.map((img, index) => (
                      <div key={index} className="w-[142px] shrink-0">
                        <div className={ClassName.photoTileWrap}>
                          <div className={ClassName.photoTile}>
                            <img 
                              src={URL.createObjectURL(img)} 
                              alt={`Car preview ${index}`} 
                              className={ClassName.fitImage} 
                            />
                          </div>
                          <button
                            type="button"
                            className={ClassName.removePhotoButton}
                            onClick={() => {
                              setImages(prev => prev.filter((_, i) => i !== index));
                            }}
                          >
                            <FiX />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className={ClassName.card}>
                <label className={ClassName.fieldLabel}>Price</label>
                <div className={ClassName.priceShell}>
                  <span className={ClassName.currency}>Br</span>
                  <input
                    className={ClassName.priceInput}
                    placeholder="5,500,000"
                    inputMode="numeric"
                    value={price}
                    onChange={(event) => setPrice(formatNumber(event.target.value))}
                  />
                </div>
                <label className={ClassName.fieldLabel}>Year of Manufacture</label>
                <select
                  className={`${ClassName.input} ${ClassName.yearInput}`}
                  value={year}
                  onChange={(event) => setYear(event.target.value)}
                >
                  <option value="">Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </section>

              <section className={ClassName.optionalCard}>
                <button
                  type="button"
                  className={ClassName.optionalToggle}
                  onClick={() => setShowDetails((value) => !value)}
                >
                  <span className={ClassName.optionalIcon}>
                    {showDetails ? "-" : "+"}
                  </span>
                  <span className="flex-1">Add full details yourself</span>
                  {showDetails ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {showDetails && (
                  <div className={ClassName.detailsGrid}>
                    <select
                      className={ClassName.input}
                      value={make}
                      onChange={(event) => setMake(event.target.value)}
                    >
                      <option value="">Car Brand</option>
                      {carMakes.map((m) => (
                        <option key={m._id} value={m._id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className={ClassName.input}
                      placeholder="Model"
                      value={model}
                      onChange={(event) => setModel(event.target.value)}
                    />
                    <select
                      className={ClassName.input}
                      value={fuelType}
                      onChange={(event) => setFuelType(event.target.value)}
                    >
                      <option value="">Fuel Type</option>
                      {fuelOptions.map((fuel) => (
                        <option key={fuel} value={fuel}>
                          {fuel}
                        </option>
                      ))}
                    </select>
                    {fuelType === "Electric" && (
                      <input
                        className={ClassName.input}
                        placeholder="KM range per charge"
                        inputMode="numeric"
                        value={batteryRangeKm}
                        onChange={(event) => setBatteryRangeKm(onlyDigits(event.target.value))}
                      />
                    )}
                    <textarea
                      className={`${ClassName.input} ${ClassName.textarea}`}
                      placeholder="Description (optional)"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </div>
                )}
              </section>

              {message && (
                <p className={ClassName.message}>
                  {message}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className={ClassName.primaryButton}
              >
                {saving
                  ? "Sending..."
                  : Boolean(dealer?.trustedDealer) &&
                    make &&
                    model &&
                    fuelType
                  ? "Post discount deal"
                  : "Send discount deal"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

const ClassName = {
  page: "min-h-screen w-screen max-w-full flex-none overflow-x-hidden bg-[#202631] font-sans text-primaryText",
  shell: "mx-auto flex min-h-screen w-full max-w-none flex-col gap-0 overflow-hidden bg-[#e8e8e8] shadow-primaryShadow sm:max-w-[390px]",
  postPage: "flex min-h-screen flex-col gap-4 bg-[#e8e8e8] pb-6",
  postHeader: "sticky top-0 z-10 grid min-h-[92px] grid-cols-[44px_1fr_44px] items-center rounded-b-[24px] bg-[#2C2B34] px-3 pb-3 pt-3 text-white shadow-sm",
  backButton: "flex h-11 w-11 items-center justify-center rounded-full text-2xl active:scale-95",
  postTitle: "text-center text-base font-semibold",
  langMini: "h-10 rounded-xl bg-white/10 text-sm font-semibold text-white px-3 active:scale-95",
  card: "mx-4 flex flex-col gap-1.5 bg-transparent",
  sectionTitle: "text-sm font-semibold text-primaryText mb-1",
  photoActionRow: "flex items-center gap-3 mt-1",
  photoActionTile: "flex h-[88px] w-[88px] cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl bg-[#FDE8E8] text-xs font-semibold text-[#E73E3E] active:scale-[0.98] transition-all border border-transparent hover:bg-[#FCD7D7]",
  photoActionAdd: "text-[#E73E3E] text-3xl font-light",
  photoActionPlus: "h-9 w-9 text-[#E73E3E]",
  photoActionCamera: "h-6 w-6 text-[#E73E3E]",
  photoTileWrap: "relative",
  photoTile: "relative flex aspect-square min-h-[88px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-gray-300 bg-white text-secondaryText shadow-sm",
  fitImage: "h-full w-full object-cover",
  removePhotoButton: "absolute right-1.5 top-1.5 z-[3] flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#EF3E3E] shadow active:scale-90",
  fieldLabel: "text-sm font-semibold text-primaryText mb-1 mt-2",
  priceShell: "flex min-h-[56px] items-center rounded-2xl border border-borderPrimary bg-white px-3 focus-within:border-primary shadow-sm",
  priceInput: "min-w-0 flex-1 bg-transparent text-base font-normal outline-none placeholder:text-base placeholder:font-normal placeholder:text-secondaryText",
  currency: "mr-3 flex h-11 w-11 items-center justify-center rounded-xl bg-bgSecondary text-sm font-semibold text-secondaryText",
  input: "min-h-[56px] rounded-2xl border border-borderPrimary bg-white px-4 py-3 text-base font-normal outline-none placeholder:font-normal focus:border-primary shadow-sm",
  yearInput: "w-full",
  optionalCard: "mx-4 mt-5 rounded-2xl border border-borderPrimary/25 bg-white shadow-sm overflow-hidden",
  optionalToggle: "flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-base font-semibold text-primaryText hover:bg-bgSecondary active:scale-[0.99] transition-all",
  optionalIcon: "flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 text-lg font-medium text-secondaryText",
  detailsGrid: "grid grid-cols-1 gap-3 border-t border-borderPrimary p-4 sm:grid-cols-2",
  textarea: "min-h-[92px] resize-y sm:col-span-2",
  primaryButton: "mx-4 mt-8 mb-8 min-h-[56px] rounded-2xl bg-primary px-5 py-4 text-lg font-bold text-white shadow-sm active:scale-[0.99] transition-all hover:bg-primary/95 disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none",
  message: "mx-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-[#EF3E3E]",
};

export default DealerDealPostPage;
